import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { CarbonFootprintData, FertigationRate } from "../../../types";
import i18n from "i18next";

interface CarbonFootprintTableState {
  tableData: CarbonFootprintData[];
}

type CFPTableValues = {
  traditional: number;
  multicoteTechnology: number;
  fertigationRating: FertigationRate;
};

type CFPTableActionPayload = PayloadAction<CFPTableValues>;

type CFPTableEntireActionPayload = PayloadAction<{
  carbonFootprint: CFPTableValues;
  nitrogenLeaching: CFPTableValues;
  nitrogenRunoff: CFPTableValues;
  nitrogenVolatilization: CFPTableValues;
  nitrogenTerrestrialEutrophication: CFPTableValues;
  // nitrogenMarinetrialEutrophication: CFPTableValues;
}>;

type CFPTableEntireLabelsActionPayload = PayloadAction<{
  carbonFootprint: { label: string; measureUnit: string };
  nitrogenLeaching: { label: string; measureUnit: string };
  nitrogenRunoff: { label: string; measureUnit: string };
  nitrogenVolatilization: { label: string; measureUnit: string };
  nitrogenTerrestrialEutrophication: { label: string; measureUnit: string };
}>;

const initialState: CarbonFootprintTableState = {
  tableData: [
    {
      environmentalParameter: i18n.t("cfpTable.tableRows.carbonFootprint.label"),
      measureUnit: i18n.t("cfpTable.tableRows.carbonFootprint.measureUnit"),
      multicoteTechnology: null,
      traditional: null,
      fertigationRating: null,
    },
    {
      environmentalParameter: i18n.t("cfpTable.tableRows.nLeaching.label"),
      measureUnit: i18n.t("cfpTable.tableRows.nLeaching.measureUnit"),
      multicoteTechnology: null,
      traditional: null,
      fertigationRating: null,
    },
    {
      environmentalParameter: i18n.t("cfpTable.tableRows.nRunOff.label"),
      measureUnit: i18n.t("cfpTable.tableRows.nRunOff.measureUnit"),
      multicoteTechnology: null,
      traditional: null,
      fertigationRating: null,
    },
    {
      environmentalParameter: i18n.t("cfpTable.tableRows.nVolatilization.label"),
      measureUnit: i18n.t("cfpTable.tableRows.nVolatilization.measureUnit"),
      multicoteTechnology: null,
      traditional: null,
      fertigationRating: null,
    },
    {
      environmentalParameter: i18n.t("cfpTable.tableRows.terrestrialEutrophication.label"),
      measureUnit: i18n.t("cfpTable.tableRows.terrestrialEutrophication.measureUnit"),
      multicoteTechnology: null,
      traditional: null,
      fertigationRating: null,
    },
  ],
};

const slice = createSlice({
  name: "CarbonFootprintTable",
  initialState,
  reducers: {
    setCarbonFootprint: (state, action: CFPTableActionPayload) => {
      state.tableData[0].traditional = action.payload.traditional;
      state.tableData[0].multicoteTechnology = action.payload.multicoteTechnology;
    },
    setNitrogenLeaching: (state, action: CFPTableActionPayload) => {
      state.tableData[1].traditional = action.payload.traditional;
      state.tableData[1].multicoteTechnology = action.payload.multicoteTechnology;
    },
    setNitrogenRunoff: (state, action: CFPTableActionPayload) => {
      state.tableData[2].traditional = action.payload.traditional;
      state.tableData[2].multicoteTechnology = action.payload.multicoteTechnology;
    },
    setNitrogenVolatilization: (state, action: CFPTableActionPayload) => {
      state.tableData[3].traditional = action.payload.traditional;
      state.tableData[3].multicoteTechnology = action.payload.multicoteTechnology;
    },
    setNitrogenTerrestrialEutrophication: (state, action: CFPTableActionPayload) => {
      state.tableData[4].traditional = action.payload.traditional;
      state.tableData[4].multicoteTechnology = action.payload.multicoteTechnology;
    },
    setEntireValues: (state, action: CFPTableEntireActionPayload) => {
      state.tableData = [
        {
          ...state.tableData[0],
          traditional: action.payload.carbonFootprint.traditional,
          multicoteTechnology: action.payload.carbonFootprint.multicoteTechnology,
          fertigationRating: action.payload.carbonFootprint.fertigationRating,
        },
        {
          ...state.tableData[1],
          traditional: action.payload.nitrogenLeaching.traditional,
          multicoteTechnology: action.payload.nitrogenLeaching.multicoteTechnology,
          fertigationRating: action.payload.nitrogenLeaching.fertigationRating,
        },
        {
          ...state.tableData[2],
          traditional: action.payload.nitrogenRunoff.traditional,
          multicoteTechnology: action.payload.nitrogenRunoff.multicoteTechnology,
          fertigationRating: action.payload.nitrogenRunoff.fertigationRating,
        },
        {
          ...state.tableData[3],
          traditional: action.payload.nitrogenVolatilization.traditional,
          multicoteTechnology: action.payload.nitrogenVolatilization.multicoteTechnology,
          fertigationRating: action.payload.nitrogenVolatilization.fertigationRating,
        },
        {
          ...state.tableData[4],
          traditional: action.payload.nitrogenTerrestrialEutrophication.traditional,
          multicoteTechnology: action.payload.nitrogenTerrestrialEutrophication.multicoteTechnology,
          fertigationRating: action.payload.nitrogenTerrestrialEutrophication.fertigationRating,
        },
      ];
    },
    setEntireLabels: (state, action: CFPTableEntireLabelsActionPayload) => {
      state.tableData = [
        {
          ...state.tableData[0],
          environmentalParameter: action.payload.carbonFootprint.label,
          measureUnit: action.payload.carbonFootprint.measureUnit,
        },
        {
          ...state.tableData[1],
          environmentalParameter: action.payload.nitrogenLeaching.label,
          measureUnit: action.payload.nitrogenLeaching.measureUnit,
        },
        {
          ...state.tableData[2],
          environmentalParameter: action.payload.nitrogenRunoff.label,
          measureUnit: action.payload.nitrogenRunoff.measureUnit,
        },
        {
          ...state.tableData[3],
          environmentalParameter: action.payload.nitrogenVolatilization.label,
          measureUnit: action.payload.nitrogenVolatilization.measureUnit,
        },
        {
          ...state.tableData[4],
          environmentalParameter: action.payload.nitrogenTerrestrialEutrophication.label,
          measureUnit: action.payload.nitrogenTerrestrialEutrophication.measureUnit,
        },
      ];
    },
  },
});

export const CFPTableActions = slice.actions;
export default slice.reducer;
